import Decimal from 'decimal.js';
import { CONTAINER_PAYLOAD_KG, freightCostPerTon, fxVsAverage } from './trade';

/**
 * XNK nhánh F (DEC-560): tỷ giá, lịch thanh toán NCC / thu tiền xuất khẩu, so sánh cước, báo cáo GĐ.
 * Hàm tính thuần: server dùng để trả số, giao diện dùng để xem trước. Bảng ở migration 0240 (DEC-537).
 */

// ───────────────────────── Tỷ giá ─────────────────────────

export const FX_SOURCES = ['vcb', 'sbv', 'customs', 'manual'] as const;
export type FxSource = (typeof FX_SOURCES)[number];
export const FX_SOURCE_LABELS: Record<FxSource, string> = {
  vcb: 'Vietcombank',
  sbv: 'NHNN (trung tâm)',
  customs: 'Hải quan',
  manual: 'Nhập tay',
};

/** Ngoại tệ theo dõi trên màn Tỷ giá: các tiền tệ đơn mua (TRADE_CURRENCIES) có trên bảng Vietcombank. */
export const FX_TRACKED = ['USD', 'CNY', 'JPY', 'KRW', 'EUR', 'THB', 'SGD'] as const;

export interface FxQuote {
  currency: string;
  /** Mua chuyển khoản: ngân hàng mua ngoại tệ của mình (dùng khi thu tiền xuất khẩu, bán ngoại tệ) */
  buyTransfer: number | null;
  /** Bán: ngân hàng bán ngoại tệ cho mình (dùng khi trả NCC) */
  sell: number | null;
}

const num = (s: unknown): number | null => {
  if (s === null || s === undefined) return null;
  const t = String(s).trim().replace(/,/g, '');
  if (!t || t === '-') return null;
  const n = Number(t);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** Đọc JSON tỷ giá Vietcombank (`/api/exchangerates?date=`): {Data:[{currencyCode, cash, transfer, sell}]}. */
export function parseVcbJson(body: unknown): FxQuote[] {
  const data = (body as { Data?: unknown; data?: unknown } | null)?.Data ?? (body as { data?: unknown } | null)?.data;
  if (!Array.isArray(data)) return [];
  const out: FxQuote[] = [];
  for (const r of data as Record<string, unknown>[]) {
    const currency = String(r.currencyCode ?? r.CurrencyCode ?? '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency) || currency === 'VND') continue;
    const q = { currency, buyTransfer: num(r.transfer ?? r.Transfer), sell: num(r.sell ?? r.Sell) };
    if (q.buyTransfer || q.sell) out.push(q);
  }
  return out;
}

/** Đọc XML tỷ giá Vietcombank cũ (pXML.aspx): <Exrate CurrencyCode="USD" Buy="…" Transfer="…" Sell="…"/>. */
export function parseVcbXml(xml: string): FxQuote[] {
  const out: FxQuote[] = [];
  for (const m of xml.matchAll(/<Exrate\b([^>]*)\/?>/gi)) {
    const attrs = Object.fromEntries([...m[1]!.matchAll(/(\w+)\s*=\s*"([^"]*)"/g)].map((a) => [a[1]!.toLowerCase(), a[2]!]));
    const currency = String(attrs.currencycode ?? '').trim().toUpperCase();
    if (!/^[A-Z]{3}$/.test(currency) || currency === 'VND') continue;
    const q = { currency, buyTransfer: num(attrs.transfer), sell: num(attrs.sell) };
    if (q.buyTransfer || q.sell) out.push(q);
  }
  return out;
}

export interface FxStats {
  last: number;
  lastDate: string;
  /** % so với bình quân (âm = đang rẻ hơn) */
  vs30: number | null;
  vs90: number | null;
  avg30: number | null;
  avg90: number | null;
  min90: number | null;
  max90: number | null;
}

/** Thống kê 1 ngoại tệ từ chuỗi tỷ giá (mới nhất trước hoặc sau đều được). */
export function fxStats(series: { date: string; rate: number }[]): FxStats | null {
  const s = series.filter((x) => x.rate > 0).sort((a, b) => b.date.localeCompare(a.date));
  if (!s.length) return null;
  const last = s[0]!;
  const window = (days: number) => {
    const from = new Date(`${last.date}T00:00:00Z`);
    from.setUTCDate(from.getUTCDate() - days);
    const f = from.toISOString().slice(0, 10);
    return s.filter((x) => x.date > f).map((x) => x.rate);
  };
  const w30 = window(30);
  const w90 = window(90);
  const a30 = fxVsAverage(last.rate, w30);
  const a90 = fxVsAverage(last.rate, w90);
  return {
    last: last.rate,
    lastDate: last.date,
    vs30: a30?.diffPct ?? null,
    vs90: a90?.diffPct ?? null,
    avg30: a30?.average ?? null,
    avg90: a90?.average ?? null,
    min90: w90.length ? Math.min(...w90) : null,
    max90: w90.length ? Math.max(...w90) : null,
  };
}

// ───────────────────────── Lịch thanh toán ─────────────────────────

export const TRADE_PAYMENT_KINDS = ['deposit', 'balance', 'lc', 'full', 'refund', 'other'] as const;
export type TradePaymentKind = (typeof TRADE_PAYMENT_KINDS)[number];
export const TRADE_PAYMENT_KIND_LABELS: Record<TradePaymentKind, string> = {
  deposit: 'Đặt cọc',
  balance: 'Trả phần còn lại',
  lc: 'Thanh toán L/C',
  full: 'Trả 100%',
  refund: 'Hoàn tiền',
  other: 'Khác',
};
export const TRADE_PAYMENT_METHODS = ['tt', 'lc', 'dp', 'da', 'cad', 'cash'] as const;
export type TradePaymentMethod = (typeof TRADE_PAYMENT_METHODS)[number];
export const TRADE_PAYMENT_METHOD_LABELS: Record<TradePaymentMethod, string> = {
  tt: 'T/T (chuyển khoản)',
  lc: 'L/C',
  dp: 'D/P',
  da: 'D/A',
  cad: 'CAD',
  cash: 'Tiền mặt',
};
export const TRADE_PAYMENT_STATUS_LABELS = { planned: 'Chờ trả', paid: 'Đã trả', cancelled: 'Đã hủy' } as const;

/** Ngưỡng % chênh với bình quân 30 ngày để gợi ý (dưới −0,5% = rẻ, trên +0,5% = đắt). */
export const FX_ADVICE_THRESHOLD_PCT = 0.5;
/** Chỉ gợi ý trả sớm khoản đến hạn trong vòng 30 ngày; ≤ 7 ngày coi là sắp đến hạn. */
export const FX_EARLY_PAY_WINDOW_DAYS = 30;
export const FX_DUE_SOON_DAYS = 7;

export type PaymentAdviceCode = 'overdue' | 'due_soon' | 'pay_early' | 'wait' | 'sell_now' | 'normal';
export interface PaymentAdvice {
  code: PaymentAdviceCode;
  label: string;
  hint: string;
  daysLeft: number;
}

const daysBetween = (from: string, to: string) => Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);

/**
 * Gợi ý cho 1 khoản chờ trả / chờ thu: so tỷ giá hôm nay với bình quân 30 ngày (fxVsAverage).
 * Trả NCC: rẻ hơn bình quân và đến hạn trong 30 ngày → nên mua ngoại tệ / trả sớm; đắt hơn và còn > 7 ngày → theo dõi thêm.
 * Thu tiền xuất khẩu: tỷ giá mua cao hơn bình quân → nên bán ngoại tệ ngay khi về.
 */
export function paymentAdvice(i: { direction: 'pay' | 'receive'; dueDate: string; today: string; vs30: number | null }): PaymentAdvice {
  const daysLeft = daysBetween(i.today, i.dueDate);
  const pct = i.vs30;
  const pctTxt = pct === null ? '' : `${pct > 0 ? '+' : ''}${pct.toLocaleString('vi-VN', { maximumFractionDigits: 2 })}%`;
  if (daysLeft < 0)
    return { code: 'overdue', label: `Quá hạn ${-daysLeft} ngày`, hint: i.direction === 'pay' ? 'Đã quá hạn trả NCC, xử lý ngay' : 'Khách nước ngoài trả trễ, nhắc khách', daysLeft };
  if (i.direction === 'receive') {
    if (pct !== null && pct >= FX_ADVICE_THRESHOLD_PCT)
      return { code: 'sell_now', label: 'Tỷ giá đang cao', hint: `Tỷ giá mua ${pctTxt} so với bình quân 30 ngày: tiền về nên bán ngoại tệ ngay`, daysLeft };
    return daysLeft <= FX_DUE_SOON_DAYS
      ? { code: 'due_soon', label: `Còn ${daysLeft} ngày`, hint: 'Sắp đến hạn thu, kiểm tra chứng từ gửi khách', daysLeft }
      : { code: 'normal', label: `Còn ${daysLeft} ngày`, hint: '', daysLeft };
  }
  if (pct !== null && pct <= -FX_ADVICE_THRESHOLD_PCT && daysLeft <= FX_EARLY_PAY_WINDOW_DAYS)
    return { code: 'pay_early', label: 'Nên trả sớm', hint: `Tỷ giá bán ${pctTxt} so với bình quân 30 ngày: mua ngoại tệ / trả sớm có lợi`, daysLeft };
  if (daysLeft <= FX_DUE_SOON_DAYS) return { code: 'due_soon', label: `Còn ${daysLeft} ngày`, hint: 'Sắp đến hạn, chuẩn bị ngoại tệ', daysLeft };
  if (pct !== null && pct >= FX_ADVICE_THRESHOLD_PCT)
    return { code: 'wait', label: 'Theo dõi thêm', hint: `Tỷ giá bán ${pctTxt} so với bình quân 30 ngày, còn ${daysLeft} ngày: chưa nên mua ngoại tệ`, daysLeft };
  return { code: 'normal', label: `Còn ${daysLeft} ngày`, hint: '', daysLeft };
}

/**
 * Chia 1 khoản ngoại tệ thành nhiều đợt theo % (VD cọc 30% + 70% còn lại): làm tròn 2 số lẻ, đợt cuối nhận phần dư
 * để tổng đúng bằng số gốc.
 */
export function splitByPercent(totalForeign: number, percents: number[]): number[] {
  if (!(totalForeign > 0)) throw new Error('Số tiền phải > 0');
  const sum = percents.reduce((s, p) => s.plus(p), new Decimal(0));
  if (!sum.eq(100)) throw new Error('Tổng % các đợt phải bằng 100');
  const total = new Decimal(totalForeign);
  let used = new Decimal(0);
  return percents.map((p, i) => {
    if (i === percents.length - 1) return total.minus(used).toNumber();
    const part = total.mul(p).div(100).toDecimalPlaces(2, Decimal.ROUND_HALF_UP);
    used = used.plus(part);
    return part.toNumber();
  });
}

// ───────────────────────── Cước ─────────────────────────

export const FREIGHT_MODES = ['fcl', 'lcl', 'road', 'air', 'bulk'] as const;
export type FreightMode = (typeof FREIGHT_MODES)[number];
export const FREIGHT_MODE_LABELS: Record<FreightMode, string> = {
  fcl: 'Cont nguyên (FCL)',
  lcl: 'Hàng lẻ (LCL)',
  road: 'Đường bộ',
  air: 'Hàng không',
  bulk: 'Tàu rời',
};
export const FREIGHT_UNIT_LABELS: Record<FreightMode, string> = { fcl: '/ cont', lcl: '/ CBM', road: '/ chuyến', air: '/ kg', bulk: '/ tấn' };
export const FREIGHT_CONTAINER_SIZES = ['20', '40', '40HC', '45', 'LCL', 'TRUCK'] as const;
/** Lãi vay năm mặc định tính chi phí vốn nằm trên đường (%). */
export const FREIGHT_DEFAULT_INTEREST_PCT = 9;
/** Xe đường bộ mặc định khi báo giá không ghi tải trọng (kg). */
export const ROAD_TRUCK_DEFAULT_KG = 30000;

export interface FreightQuoteForCompare {
  mode: FreightMode;
  containerSize: string | null;
  freightForeign: number;
  localChargesVnd: number;
  inlandVnd: number;
  capacityKg: number | null;
  transitDays: number | null;
}

/**
 * Số đơn vị tính cước cho 1 lô: FCL / đường bộ = số cont / chuyến (làm tròn lên theo tải trọng),
 * LCL = số CBM (tối thiểu 1), hàng không = kg, tàu rời = tấn.
 * Giả định (ghi ở DEC-560): phí local + xe về kho của FCL / đường bộ tính theo cont / chuyến; LCL, hàng không, tàu rời tính 1 lần cho lô.
 */
export function freightUnits(q: FreightQuoteForCompare, cargoKg: number, cargoCbm: number | null): { units: number; perUnitCharges: boolean } {
  if (q.mode === 'fcl' || q.mode === 'road') {
    const cap = q.capacityKg ?? (q.mode === 'road' ? ROAD_TRUCK_DEFAULT_KG : (CONTAINER_PAYLOAD_KG[q.containerSize ?? '20'] ?? CONTAINER_PAYLOAD_KG['20']!));
    return { units: Math.max(1, Math.ceil(cargoKg / cap)), perUnitCharges: true };
  }
  if (q.mode === 'lcl') return { units: Math.max(1, cargoCbm ?? 1), perUnitCharges: false };
  if (q.mode === 'air') return { units: cargoKg, perUnitCharges: false };
  return { units: cargoKg / 1000, perUnitCharges: false };
}

export interface FreightCompareRow {
  units: number;
  totalVnd: number;
  logisticsPerTon: number;
  capitalPerTon: number;
  totalPerTon: number;
}

/** Chi phí về kho / tấn của 1 báo giá cho 1 lô: gọi freightCostPerTon (gồm chi phí vốn trên đường). */
export function freightCompareRow(
  q: FreightQuoteForCompare,
  lot: { cargoKg: number; cargoCbm: number | null; cargoValueVnd: number; annualInterestPct: number },
  rate: number,
): FreightCompareRow {
  const { units, perUnitCharges } = freightUnits(q, lot.cargoKg, lot.cargoCbm);
  const mul = perUnitCharges ? units : 1;
  const r = freightCostPerTon({
    freightForeign: new Decimal(q.freightForeign).mul(units).toNumber(),
    rate,
    localChargesVnd: q.localChargesVnd * mul,
    inlandVnd: q.inlandVnd * mul,
    cargoKg: lot.cargoKg,
    transitDays: q.transitDays ?? 0,
    cargoValueVnd: lot.cargoValueVnd,
    annualInterestPct: lot.annualInterestPct,
  });
  const totalVnd = new Decimal(q.freightForeign).mul(units).mul(rate).plus(q.localChargesVnd * mul).plus(q.inlandVnd * mul).toDecimalPlaces(0, Decimal.ROUND_HALF_UP).toNumber();
  return { units, totalVnd, ...r };
}
